import { lazy } from 'react'

import { Route, Routes } from 'react-router'

import type { Network } from '@bettermode/common/gql/generated'
import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useTimeFrame } from '@tribeplatform/react-components/Analytics/hooks'
import { useEventModuleAvailability } from '@tribeplatform/react-components/Event/hooks'
import { T } from '@tribeplatform/react-components/i18n'
import { useRouter, useRouterPath } from '@tribeplatform/react-sdk'

import { EventsAnalytics } from './NetworkAnalytics/EventsAnalytics.js'

const MessagesAnalytics = lazy(() =>
  import('./NetworkAnalytics/MessagesAnalytics.js').then(m => ({
    default: m.MessagesAnalytics,
  })),
)
const OverviewAnalytics = lazy(() =>
  import('./NetworkAnalytics/OverviewAnalytics.js').then(m => ({
    default: m.OverviewAnalytics,
  })),
)
const PeopleAnalytics = lazy(() =>
  import('./NetworkAnalytics/PeopleAnalytics.js').then(m => ({
    default: m.PeopleAnalytics,
  })),
)
const PostsAnalytics = lazy(() =>
  import('./NetworkAnalytics/PostsAnalytics.js').then(m => ({
    default: m.PostsAnalytics,
  })),
)
const ReachAnalytics = lazy(() =>
  import('./NetworkAnalytics/ReachAnalytics.js').then(m => ({
    default: m.ReachAnalytics,
  })),
)
const SpacesAnalytics = lazy(() =>
  import('./NetworkAnalytics/SpacesAnalytics.js').then(m => ({
    default: m.SpacesAnalytics,
  })),
)
const AuditLogs = lazy(() =>
  import('../Admin/AuditLogs/index.js').then(m => ({
    default: m.AuditLogs,
  })),
)

interface Props {
  networkId: Network['id']
  networkCreatedAt: Network['createdAt']
}

export const Analytics = ({ networkId, networkCreatedAt }: Props) => {
  const { timeFrame, TimeFrameSelect } = useTimeFrame({
    createdAt: networkCreatedAt,
  })
  const { isEventModuleEnabledOnNetwork } = useEventModuleAvailability()

  return (
    <Routes>
      <Route
        path="analytics"
        element={
          <OverviewAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route
        path="analytics/dashboard"
        element={
          <OverviewAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route
        path="analytics/people"
        element={
          <PeopleAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route
        path="analytics/posts"
        element={
          <PostsAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      {isEventModuleEnabledOnNetwork && (
        <Route path="analytics/events" element={<EventsAnalytics />} />
      )}
      <Route
        path="analytics/spaces"
        element={
          <SpacesAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route
        path="analytics/reach"
        element={
          <ReachAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route
        path="analytics/messages"
        element={
          <MessagesAnalytics
            networkId={networkId}
            timeFrame={timeFrame}
            TimeFrameSelect={TimeFrameSelect}
          />
        }
      />
      <Route path="audit-logs" element={<AuditLogs />} />
    </Routes>
  )
}

export function AnalyticsBreadCrumb() {
  const pathname = useRouterPath()
  const { Link } = useRouter()

  switch (pathname) {
    case adminRoutes.reports.analytics.root:
      return (
        <Link href={adminRoutes.reports.analytics.root}>
          <T id="Generics.Overview" defaultMessage="Overview" />
        </Link>
      )
    case adminRoutes.reports.analytics.people:
      return (
        <Link href={adminRoutes.reports.analytics.people}>
          <T id="Generics.People" defaultMessage="People" />
        </Link>
      )
    case adminRoutes.reports.analytics.posts:
      return (
        <Link href={adminRoutes.reports.analytics.posts}>
          <T id="Generics.Posts" defaultMessage="Posts" />
        </Link>
      )
    case adminRoutes.reports.analytics.spaces:
      return (
        <Link href={adminRoutes.reports.analytics.spaces}>
          <T id="Generics.Spaces" defaultMessage="{SPACES_CC}" />
        </Link>
      )
    case adminRoutes.reports.analytics.reach:
      return (
        <Link href={adminRoutes.reports.analytics.reach}>
          <T
            id="Generics.ReachAndEngagement"
            defaultMessage="Reach & engagement"
          />
        </Link>
      )
    case adminRoutes.reports.analytics.messages:
      return (
        <Link href={adminRoutes.reports.analytics.messages}>
          <T id="Generics.Messages" defaultMessage="Messages" />
        </Link>
      )
    case adminRoutes.reports.auditLogs:
      return (
        <Link href={adminRoutes.reports.auditLogs}>
          <T id="Generics.AuditLogs" defaultMessage="Audit logs" />
        </Link>
      )
    default:
      return (
        <Link href={adminRoutes.reports.analytics.root}>
          <T id="Generics.Analytics" defaultMessage="Analytics" />
        </Link>
      )
  }
}
